import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RefreshIcon from '@mui/icons-material/Refresh';
import React, { useEffect } from 'react';
import { useNavigate, useRouteError } from 'react-router-dom';

import { SimpleAppBar } from '../../components/AppBar/SimpleAppBar';
import { Box } from '../../components/Box/Box';
import { Button } from '../../components/Button/Button';
import { ListItemIcon } from '../../components/ListItemIcon/ListItemIcon';
import { ListItemText } from '../../components/ListItemText/ListItemText';
import { MenuItem } from '../../components/MenuItem/MenuItem';
import { Stack } from '../../components/Stack/Stack';
import { TextField } from '../../components/TextField/TextField';
import { Typography } from '../../components/Typography/Typography';
import { logError } from '../../utils/logging';

import { reportException } from '../../utils/sentryService';
import { getFullAppRoutePath, NavItem } from '../Nav/nav.types';

export interface RouteErrorBoundaryProps {
  appBarTitle?: string;

  /**
   * @default false
   */
  disableAppBar?: boolean;

  /**
   * @default false
   */
  disableErrorReporting?: boolean;

  /**
   * @default 'Unexpected Error'
   */
  errorTitle?: string;

  navItems?: NavItem[];
}

export const RouteErrorBoundary = (props: RouteErrorBoundaryProps) => {
  const { appBarTitle, disableAppBar, disableErrorReporting, navItems, errorTitle } = props;

  const error = useRouteError();
  useEffect(() => {
    logError(error);

    if (!disableErrorReporting && error instanceof Error) {
      reportException(error);
    }
    // TODO - if error is a class that we expect, we should try and display a message using our custom Error
  }, [error, disableErrorReporting]);

  // TODO - implement stateful Reload and Go Back
  //      - There isn't a feature baked into react router that supports true stateful reloading and "go back"
  //      - Some solutions seem to recommend a custom react hook for persisting stateful history in local storage
  //        via the useLocation
  //      - we should at least minimally improve reload such that it doesn't reload the full app
  const navigate = useNavigate();

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        width: '100%'
      }}>
      {!disableAppBar && <SimpleAppBar title={appBarTitle || ''} />}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flex: '1',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'scroll'
        }}>
        <Stack spacing={2} sx={{ alignItems: 'center' }}>
          <Typography variant='h3' text={errorTitle || 'Unexpected Error'} />
          {/* On small screens, give the users a quick Jump to navigation */}
          {/* if we ever decide to do a dashboard route, we could just give them a button to go there too */}
          {!!navItems?.length && (
            <TextField
              color='primary'
              fullWidth
              select
              label='Jump to...'
              onChange={(e) => e.target.value && navigate(e.target.value)}
              sx={{ display: { xs: 'flex', sm: 'none' } }}>
              {navItems.map((navItem) => (
                <MenuItem key={navItem.routePath} value={getFullAppRoutePath(navItem.routePath)}>
                  <ListItemIcon>{navItem.icon}</ListItemIcon>
                  <ListItemText>{navItem.title}</ListItemText>
                </MenuItem>
              ))}
            </TextField>
          )}
          <Stack direction='row' spacing={2}>
            <Button
              color='primary'
              onClick={() => navigate(-1)}
              startIcon={<ArrowBackIcon />}
              text='Back'
              variant='contained'
            />
            <Button
              color='primary'
              onClick={() => navigate(0)}
              startIcon={<RefreshIcon />}
              text='Reload'
              variant='outlined'
            />
          </Stack>
        </Stack>
      </Box>
    </Box>
  );
};
